import { DEFAULT_BPM, type BpmSource, type TrackAudioProfile } from '../types';

interface SpotifyAudioFeatures {
  tempo?: number;
  energy?: number;
  danceability?: number;
  valence?: number;
  loudness?: number;
  time_signature?: number;
}

interface SpotifyAudioAnalysis {
  track?: {
    tempo?: number;
    loudness?: number;
    time_signature?: number;
  };
  beats?: { start?: number; confidence?: number }[];
}

export const FALLBACK_AUDIO_PROFILE: TrackAudioProfile = {
  bpm: DEFAULT_BPM,
  bpmSource: 'fallback',
  confidence: 0.25,
  energy: 0.62,
  danceability: 0.68,
  valence: 0.5,
  loudness: -8,
  timeSignature: 4,
  beatOffsetMs: 0,
};

export function clampBpm(value: number | undefined): number | undefined {
  if (!value || Number.isNaN(value)) return undefined;
  return Math.max(60, Math.min(220, Math.round(value)));
}

export function makeManualAudioProfile(bpm: number, source: BpmSource = 'manual'): TrackAudioProfile {
  return {
    ...FALLBACK_AUDIO_PROFILE,
    bpm: clampBpm(bpm) ?? DEFAULT_BPM,
    bpmSource: source,
    confidence: 1,
  };
}

export function makeSpotifyAudioProfile(
  features: SpotifyAudioFeatures | null | undefined,
  analysis: SpotifyAudioAnalysis | null | undefined
): TrackAudioProfile {
  const featureBpm = clampBpm(features?.tempo);
  const analysisBpm = clampBpm(analysis?.track?.tempo);
  const bpm = featureBpm ?? analysisBpm ?? DEFAULT_BPM;
  const bpmSource: BpmSource = featureBpm
    ? 'spotify-features'
    : analysisBpm
      ? 'spotify-analysis'
      : 'fallback';

  return {
    bpm,
    bpmSource,
    confidence: bpmSource === 'fallback' ? 0.25 : featureBpm ? 0.95 : 0.8,
    energy: clamp01(features?.energy, FALLBACK_AUDIO_PROFILE.energy),
    danceability: clamp01(features?.danceability, FALLBACK_AUDIO_PROFILE.danceability),
    valence: clamp01(features?.valence, FALLBACK_AUDIO_PROFILE.valence),
    loudness: clampLoudness(features?.loudness ?? analysis?.track?.loudness),
    timeSignature: clampTimeSignature(features?.time_signature ?? analysis?.track?.time_signature),
    beatOffsetMs: clampBeatOffset(analysis?.beats?.[0]?.start),
  };
}

export function intensityFromAudioProfile(profile: TrackAudioProfile): number {
  const loudnessEnergy = Math.max(0, Math.min(1, (profile.loudness + 24) / 18));
  return Math.max(0.35, Math.min(1.25, profile.energy * 0.62 + profile.danceability * 0.24 + loudnessEnergy * 0.14));
}

function clamp01(value: number | undefined, fallback: number): number {
  if (typeof value !== 'number' || Number.isNaN(value)) return fallback;
  return Math.max(0, Math.min(1, value));
}

function clampLoudness(value: number | undefined): number {
  if (typeof value !== 'number' || Number.isNaN(value)) return FALLBACK_AUDIO_PROFILE.loudness;
  return Math.max(-60, Math.min(0, value));
}

function clampTimeSignature(value: number | undefined): number {
  if (!value || Number.isNaN(value)) return FALLBACK_AUDIO_PROFILE.timeSignature;
  return Math.max(3, Math.min(7, Math.round(value)));
}

function clampBeatOffset(startSeconds: number | undefined): number {
  if (typeof startSeconds !== 'number' || Number.isNaN(startSeconds)) return 0;
  return Math.max(0, Math.min(8000, Math.round(startSeconds * 1000)));
}
